import {
  formatRoomData,
  sortRooms,
  useExamEventDetails,
  formatExamDateOnly,
  formatExamTime,
  DOWNLOADABLE_COURSE_STATUSES,
} from '@exam-center/exam-event-management';
import { ExamType } from '@exam-center/graphql';
import { Box, Divider, Flex, Grid, Paper, Text } from '@shared/components';
import { adjustTime, formatDateTime } from '@shared/utils';
import { Fragment, type ReactNode, useMemo } from 'react';

interface ExamScheduleSummaryProps {
  endAdornment?: ReactNode;
}

export default function ExamScheduleSummary(props: Readonly<ExamScheduleSummaryProps>) {
  const { endAdornment } = props;
  const { examEvent } = useExamEventDetails();
  const {
    courses,
    numberOfExaminees,
    seat,
    examStartedAt,
    examEndedAt,
    examLocation,
    rooms,
    roundNumber,
    postponeStartDurationInMs,
    postponeEndDurationInMs,
    postponementRemark,
    examType,
    downloadedUser,
    metadata: { lastDownloadedAt: downloadedAt },
  } = examEvent;
  const {
    nameTh: locationName,
    province: { nameTh: provinceName },
  } = examLocation;

  const examRooms = useMemo(() => rooms?.map(formatRoomData).sort(sortRooms) ?? [], [rooms]);

  const sessionInfo: {
    label: string;
    value: ReactNode;
  }[] = useMemo(() => {
    const baseInfo = [
      {
        label: 'รอบสอบที่:',
        value: roundNumber,
      },
      {
        label: 'วันที่สอบ:',
        value: formatExamDateOnly(examStartedAt),
      },
      {
        label: 'เวลาสอบ:',
        value: (() => {
          const adjustedStartTime = adjustTime(examStartedAt, postponeStartDurationInMs ?? 0);
          const adjustedEndTime = adjustTime(examEndedAt, postponeEndDurationInMs ?? 0);
          return (
            <Flex
              sx={(theme) => ({
                flexDirection: 'column',
                gap: theme.spacing(1),
              })}
            >
              <Text variant='body1Semibold'>
                {formatExamTime(adjustedStartTime, adjustedEndTime)}
              </Text>
              {postponeStartDurationInMs || postponeEndDurationInMs ? (
                <Box>
                  <Text variant='body2'>
                    (เวลาเดิม: {formatExamTime(examStartedAt, examEndedAt)})
                  </Text>
                  {postponementRemark ? (
                    <Text variant='body2'>เหตุผลในการเลื่อน: {postponementRemark}</Text>
                  ) : null}
                </Box>
              ) : null}
            </Flex>
          );
        })(),
      },
      {
        label: 'สถานที่สอบ:',
        value: (
          <Flex
            sx={(theme) => ({
              flexDirection: 'column',
              gap: theme.spacing(1),
            })}
          >
            <Text variant='body1Semibold'>{locationName}</Text>
            <Box>
              {examRooms.map((room) => (
                <Text key={room.roomNumber} variant='body2'>
                  อาคาร: {room.building} ชั้น: {room.floor} ห้อง: {room.name} เลขห้อง:{' '}
                  {room.roomNumber}
                </Text>
              ))}
            </Box>
          </Flex>
        ),
      },
      {
        label: 'จังหวัด:',
        value: provinceName,
      },
    ];

    if (examType === ExamType.Computer) return baseInfo;

    const isExamReady = courses.every((course) =>
      DOWNLOADABLE_COURSE_STATUSES.includes(course.status),
    );

    return [
      ...baseInfo,
      {
        label: 'สถานะชุดข้อสอบ:',
        value: (
          <Flex
            sx={(theme) => ({
              flexDirection: 'column',
              gap: theme.spacing(1),
            })}
          >
            <Text variant='body1Semibold'>{isExamReady ? 'พร้อมใช้งาน' : 'ยังไม่พร้อมใช้งาน'}</Text>
            {downloadedUser && downloadedAt ? (
              <Text variant='body2'>
                ดาวน์โหลดชุดข้อสอบล่าสุด: {formatDateTime(downloadedAt)} โดย{' '}
                {downloadedUser.firstname} {downloadedUser.lastname}
              </Text>
            ) : null}
          </Flex>
        ),
      },
    ];
  }, [
    courses,
    examRooms,
    examType,
    locationName,
    provinceName,
    examStartedAt,
    examEndedAt,
    roundNumber,
    postponeStartDurationInMs,
    postponeEndDurationInMs,
    postponementRemark,
    downloadedUser,
    downloadedAt,
  ]);

  return (
    <Paper
      sx={(theme) => ({
        p: theme.spacing(6),
        display: 'flex',
        borderRadius: theme.shape.rounded(2),
        justifyContent: 'space-between',
      })}
    >
      <Flex
        sx={(theme) => ({
          gap: theme.spacing(6),
          flexGrow: 1,
          alignItems: 'center',
        })}
      >
        <Flex
          sx={(theme) => ({
            p: theme.spacing(4),
            flexDirection: 'column',
            gap: theme.spacing(2),
            justifyContent: 'center',
            borderRadius: theme.shape.rounded(2),
            backgroundColor: theme.palette.primary.lighter,
            height: '100%',
          })}
        >
          <Text
            sx={(theme) => ({
              color: theme.palette.text.secondary,
            })}
          >
            จำนวนผู้สมัคร / ที่นั่งที่เปิดรับ
          </Text>
          <Text variant='h3'>
            {numberOfExaminees}/{seat} ที่นั่ง
          </Text>
        </Flex>
        <Divider orientation='vertical' />
        <Grid
          sx={(theme) => ({
            gridTemplateColumns: '120px 1fr',
            rowGap: theme.spacing(2),
            columnGap: theme.spacing(6),
          })}
        >
          {sessionInfo.map((info) => (
            <Fragment key={info.label}>
              <Text>{info.label}</Text>
              {typeof info.value === 'object' ? (
                info.value
              ) : (
                <Text variant='body1Semibold'>{info.value}</Text>
              )}
            </Fragment>
          ))}
        </Grid>
      </Flex>
      {endAdornment}
    </Paper>
  );
}
